import { http } from '@/lib/http';
import type {
  AmendGroupVoucherInput,
  GroupVoucherReadResult,
  GroupVoucherResult,
  GroupVoucherView,
  IssueGroupVoucherInput,
  ReissueGroupVoucherInput,
} from './voucher-types';

// The operator-bff PNR-group voucher routes (#797 slice 1). `http` is the single
// configured ky instance per frontend-conventions §6.4 — never a bare fetch.
// NOTE the missing leading slash: ky's `prefixUrl` requires relative paths.

function voucherPath(caseUrn: string, subCaseUrn: string): string {
  return `v1/cases/${encodeURIComponent(caseUrn)}/pnr-groups/${encodeURIComponent(subCaseUrn)}/voucher`;
}

/**
 * `operator-bff:GET pnr-group voucher@v1` — the group's program, or `null` while
 * none has been issued (a 200 state by contract, so no error path to untangle).
 */
export async function fetchGroupVoucher(
  caseUrn: string,
  subCaseUrn: string,
): Promise<GroupVoucherView | null> {
  const result = await http.get(voucherPath(caseUrn, subCaseUrn)).json<GroupVoucherReadResult | null>();
  return result?.voucher ?? null;
}

/** Idempotent ensure (200 on create AND on retry) — re-POSTing returns the existing program. */
export async function issueGroupVoucher(
  caseUrn: string,
  subCaseUrn: string,
  input: IssueGroupVoucherInput,
): Promise<GroupVoucherView> {
  const result = await http
    .post(voucherPath(caseUrn, subCaseUrn), { json: input })
    .json<GroupVoucherResult>();
  return result.voucher;
}

/** Idempotent cancel — `reason` is mandatory at the BFF edge (400 `reason_required`). */
export async function cancelGroupVoucher(
  caseUrn: string,
  subCaseUrn: string,
  reason: string,
): Promise<GroupVoucherView> {
  const result = await http
    .delete(voucherPath(caseUrn, subCaseUrn), { json: { reason } })
    .json<GroupVoucherResult>();
  return result.voucher;
}

/** Cancel ONE code (#826) — the program stays alive for the rest of the group. */
export async function cancelGroupVoucherCode(
  caseUrn: string,
  subCaseUrn: string,
  codeId: string,
  reason: string,
): Promise<GroupVoucherView> {
  const result = await http
    .delete(`${voucherPath(caseUrn, subCaseUrn)}/codes/${encodeURIComponent(codeId)}`, { json: { reason } })
    .json<GroupVoucherResult>();
  return result.voucher;
}

/** In-place amendment (M4): the live program keeps its codes — codes and claims survive. */
export async function amendGroupVoucher(
  caseUrn: string,
  subCaseUrn: string,
  input: AmendGroupVoucherInput,
): Promise<GroupVoucherView> {
  const result = await http
    .post(`${voucherPath(caseUrn, subCaseUrn)}/amend`, { json: input })
    .json<GroupVoucherResult>();
  return result.voucher;
}

/** Hotel reassignment: cancel at the vendor + re-create against the new destination. */
export async function reissueGroupVoucher(
  caseUrn: string,
  subCaseUrn: string,
  input: ReissueGroupVoucherInput,
): Promise<GroupVoucherView> {
  const result = await http
    .post(`${voucherPath(caseUrn, subCaseUrn)}/reissue`, { json: input })
    .json<GroupVoucherResult>();
  return result.voucher;
}

/** The domain's resume hook — finish asynchronous code generation now. */
export async function syncGroupVoucherCodes(
  caseUrn: string,
  subCaseUrn: string,
): Promise<GroupVoucherView> {
  const result = await http
    .post(`${voucherPath(caseUrn, subCaseUrn)}/sync-codes`)
    .json<GroupVoucherResult>();
  return result.voucher;
}
